// Call transcript — JustCall AI speaker turns + summary + notes; fetch from JustCall when configured.
import { useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { T2Panel, T2Label } from "../quotes/_t2";

export default function CallTranscript({ doc, jcData = {}, jcAi = {}, info = {}, onReload }) {
  const [busy, setBusy] = useState(false);
  const lines = doc.Transcript || [];
  const legacy = jcData.transcript || jcData.transcription;
  const summary = doc.JCAISummary || jcAi.summary;
  const speakers = [...new Set(lines.map((l) => l.speaker))];
  const fetchIt = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/calls/${doc._id}/transcript/fetch`);
      if (data.ok) toast.success(`Transcript fetched · ${data.lines} lines · re-analysed`); else toast.error(data.reason || "No transcript available");
      await onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fetch failed"); } finally { setBusy(false); }
  };
  return (
    <T2Panel icon={FileText} title="Transcript & notes" testid="panel-transcript"
      right={<button type="button" onClick={fetchIt} disabled={busy} className="t2-btn-ghost" style={{ fontSize: 10, padding: "3px 8px" }} data-testid="transcript-fetch-btn">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {lines.length ? "Refresh from JustCall" : "Fetch from JustCall"}
      </button>}>
      {summary && (
        <div style={{ marginBottom: 10 }} data-testid="transcript-summary">
          <T2Label>JustCall AI summary</T2Label>
          <div style={{ fontSize: 12.5, color: "var(--t2-grey1)", whiteSpace: "pre-wrap" }}>{summary}</div>
        </div>
      )}
      {lines.length > 0 ? (
        <div className="t2-scroll" style={{ maxHeight: 520, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, paddingRight: 6 }} data-testid="transcript-lines">
          {lines.map((l, i) => {
            const right = speakers.indexOf(l.speaker) === 1;
            return (
              <div key={i} style={{ alignSelf: right ? "flex-end" : "flex-start", maxWidth: "82%" }}>
                <div className="t2-mono" style={{ fontSize: 9.5, color: "var(--t2-grey3)", textAlign: right ? "right" : "left" }}>
                  {l.speaker || "Speaker"}{l.ts != null ? ` · ${l.ts}` : ""}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.45, color: "var(--t2-grey1)", padding: "6px 10px", borderRadius: 10,
                  background: right ? "color-mix(in srgb, var(--t2-teal-ink) 12%, transparent)" : "var(--t2-surface2)" }}>{l.text}</div>
              </div>
            );
          })}
        </div>
      ) : legacy ? (
        <div className="t2-scroll" style={{ fontSize: 13, color: "var(--t2-grey1)", whiteSpace: "pre-wrap", lineHeight: 1.5, maxHeight: 520, overflowY: "auto" }}>{legacy}</div>
      ) : (
        <div style={{ fontSize: 12, color: "var(--t2-grey3)" }} data-testid="transcript-empty">
          No transcript on this call yet{doc.TranscriptError ? ` — ${doc.TranscriptError}` : ""}. Transcripts come from the JustCall AI API once it's connected.
        </div>
      )}
      {(doc.Notes || info.notes) && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--t2-border)" }} data-testid="transcript-notes">
          <T2Label>Notes</T2Label>
          <div style={{ fontSize: 12.5, color: "var(--t2-grey1)", whiteSpace: "pre-wrap" }}>{doc.Notes}</div>
          {info.notes && info.notes !== doc.Notes && <div style={{ fontSize: 12.5, color: "var(--t2-grey2)", whiteSpace: "pre-wrap", marginTop: 6 }}>{info.notes}</div>}
        </div>
      )}
    </T2Panel>
  );
}
